import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { useBulkSend } from "src/context/BulkSendContext";
import "./bulkSendWidget.scss";

// Lives once at the app root (see App.tsx) so it survives page navigation — a bulk send keeps
// running and reporting progress no matter which page the user moves to next.
export default function BulkSendWidget() {
    const { active, completed, queueLength, cancelActive, dismissCompleted } = useBulkSend();
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    if (active) {
        const pct = active.total > 0 ? Math.min(100, Math.round((active.done / active.total) * 100)) : 0;
        return (
            <div className="bulk-send-widget">
                <div className="bulk-send-widget__row">
                    <span className="bulk-send-widget__spinner" />
                    <div className="bulk-send-widget__body">
                        <span className="bulk-send-widget__label">
                            {t("common.bulkSendingProgress", { subject: active.subject, done: active.done, total: active.total })}
                        </span>
                        <div className="bulk-send-widget__bar">
                            <div
                                className="bulk-send-widget__bar-fill"
                                style={{ "--bulk-send-pct": `${pct}%` } as React.CSSProperties}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className="bulk-send-widget__cancel"
                        onClick={cancelActive}
                        disabled={active.cancelRequested}
                    >
                        {active.cancelRequested ? t("common.cancelling") : t("common.cancel")}
                    </button>
                </div>
                {queueLength > 0 && (
                    <span className="bulk-send-widget__queued">
                        {t("common.bulkSendMoreQueued", { count: queueLength })}
                    </span>
                )}
            </div>
        );
    }

    if (completed) {
        const hasDetails = (completed.result.details?.length ?? 0) > 0;
        return (
            <div className={`bulk-send-widget bulk-send-widget--${completed.result.variant}`}>
                <button
                    type="button"
                    className="bulk-send-widget__result"
                    onClick={() => hasDetails && setExpanded((v) => !v)}
                >
                    <span className="bulk-send-widget__label">{completed.result.message}</span>
                    {hasDetails && (expanded ? <TbChevronUp /> : <TbChevronDown />)}
                </button>
                {hasDetails && expanded && (
                    <ul className="bulk-send-widget__details">
                        {completed.result.details!.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                )}
                <button type="button" className="bulk-send-widget__dismiss" onClick={dismissCompleted}>
                    <IoMdClose />
                </button>
            </div>
        );
    }

    return null;
}
